import { useEffect, useState } from "react";
import BootstrapModal from "react-bootstrap/Modal";
import Select, { components, OptionProps } from "react-select";
import { toast } from "react-toastify";
import actionsApi, { RejectionReasonOption } from "../services/actionsApi";
import { Row, StatusAction } from "../types";
import { formatCell, pick, rowText } from "../utils";
import useLang from "Shared/hooks/useLanguage";
import { LoaderIcon } from "Shared/Icons";
import { styles } from "Utils/Common";

const TITLES: Record<StatusAction, string> = {
  [StatusAction.Hold]: "Put On Hold",
  [StatusAction.Complete]: "Complete",
  [StatusAction.Reject]: "Reject",
  [StatusAction.Delete]: "Delete Requisition",
};

interface Props {
  action: StatusAction | null;
  rows: Row[]; // requisitions the status change applies to
  onClose: () => void;
  // reason = ActionReasons; rejectComment only for Reject
  onSubmit: (reason: string, rejectComment?: string) => Promise<void>;
}

// Reason prompt for StatusChangedV2 — same rules as v1: free-text reason, or for Reject a
// reason from the rejection lookup (add / delete supported) plus an optional comment.
const StatusChangeModal = ({ action, rows, onClose, onSubmit }: Props) => {
  const { t } = useLang();
  const [reason, setReason] = useState("");
  const [rejectReason, setRejectReason] = useState<RejectionReasonOption | null>(null);
  const [rejectSubject, setRejectSubject] = useState("");
  const [reasons, setReasons] = useState<RejectionReasonOption[]>([]);
  const [adding, setAdding] = useState(false);
  const [newSubject, setNewSubject] = useState("");
  const [newDescription, setNewDescription] = useState("");
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);
  const isReject = action === StatusAction.Reject;

  const loadReasons = () =>
    actionsApi
      .rejectionReasons()
      .then(setReasons)
      .catch(() => setReasons([]));

  useEffect(() => {
    if (action === null) return;
    setReason("");
    setRejectReason(null);
    setRejectSubject("");
    setAdding(false);
    setNewSubject("");
    setNewDescription("");
    setError("");
    if (action === StatusAction.Reject) loadReasons();
  }, [action]);

  const pickReason = async (o: RejectionReasonOption | null) => {
    setRejectReason(o);
    setRejectSubject("");
    if (!o) return;
    try {
      setRejectSubject(await actionsApi.rejectionReasonSubject(o.label));
    } catch {
      setRejectSubject(o.label);
    }
  };

  const deleteReason = async (o: RejectionReasonOption) => {
    try {
      await actionsApi.deleteRejectionReason(o.value);
      toast.success(t("Reason deleted"));
      if (rejectReason?.value === o.value) pickReason(null);
      loadReasons();
    } catch (e) {
      toast.error((e as Error).message);
    }
  };

  const addReason = async () => {
    if (!newSubject.trim() || !newDescription.trim())
      return setError(t("Subject and description are required."));
    try {
      await actionsApi.addRejectionReason(newSubject.trim(), newDescription.trim());
      toast.success(t("Reason added"));
      setAdding(false);
      setNewSubject("");
      setNewDescription("");
      setError("");
      loadReasons();
    } catch (e) {
      toast.error((e as Error).message);
    }
  };

  const save = async () => {
    if (
      action === StatusAction.Hold &&
      rows.some((r) => rowText(r, "RequisitionStatus").toLowerCase() === "on hold")
    )
      return setError(t("Cannot change status of On Hold Requisition"));
    if (isReject && !rejectReason) return setError(t("Please Select Reason"));
    if (!isReject && !reason.trim()) return setError(t("Please Enter Reason"));
    setSaving(true);
    try {
      await onSubmit(
        isReject ? rejectSubject || rejectReason!.label : reason.trim(),
        isReject ? reason.trim() : undefined
      );
    } finally {
      setSaving(false);
    }
  };

  // Option with a delete (×) button, like v1.
  const ReasonOption = (props: OptionProps<RejectionReasonOption, false>) => (
    <components.Option {...props}>
      <div className="d-flex justify-content-between align-items-center gap-2">
        <span>{props.data.label}</span>
        <button
          type="button"
          className="btn btn-icon btn-sm h-20px w-20px btn-active-light-danger"
          aria-label={t("Delete reason")}
          onMouseDown={(e) => e.stopPropagation()}
          onClick={(e) => {
            e.stopPropagation();
            deleteReason(props.data);
          }}
        >
          <i className="bi bi-x text-danger"></i>
        </button>
      </div>
    </components.Option>
  );

  return (
    <BootstrapModal
      show={action !== null}
      onHide={onClose}
      backdrop="static"
      keyboard={false}
      centered
      dialogClassName="vr2-cm-dialog"
      contentClassName="vr2-cm-content"
    >
      <BootstrapModal.Header closeButton className="py-4 px-6">
        <BootstrapModal.Title className="h5 m-0">
          {action !== null ? t(TITLES[action]) : ""}
          <span className="text-gray-500 fw-normal">
            {" "}
            · {rows.length} {t(rows.length === 1 ? "requisition" : "requisitions")}
          </span>
        </BootstrapModal.Title>
      </BootstrapModal.Header>
      <BootstrapModal.Body className="px-6 py-4">
        {/* Selected orders (Order | Added Date | Requisition Type) */}
        <div className="vr2-sc-list mb-4">
          {rows.slice(0, 8).map((r) => (
            <div key={String(pick(r, "__RowKey", "RequisitionOrderId"))} className="vr2-sc-row">
              <span className="fw-semibold">{rowText(r, "Order", "OrderNumber")}</span>
              <span className="text-gray-600">{formatCell(pick(r, "AddedDate"))}</span>
              <span className="text-gray-600 text-truncate">{rowText(r, "RequisitionType")}</span>
            </div>
          ))}
          {rows.length > 8 && (
            <div className="text-gray-500 fs-7 mt-1">
              +{rows.length - 8} {t("more")}
            </div>
          )}
        </div>

        {isReject ? (
          <>
            <label className="mb-2 required form-label">{t("Select Reason")}</label>
            <Select
              options={reasons}
              value={rejectReason}
              placeholder={t("Select Reason")}
              theme={(theme) => styles(theme)}
              isSearchable
              menuPortalTarget={document.body}
              components={{ Option: ReasonOption }}
              styles={{ menuPortal: (base) => ({ ...base, zIndex: 2000 }) }}
              onChange={(o) => pickReason(o as RejectionReasonOption)}
            />
            {rejectSubject && (
              <div className="mt-2 fs-7">
                <span className="fw-bold">{t("Reason")}:</span>{" "}
                <span className="text-danger">{rejectSubject}</span>
              </div>
            )}

            {adding ? (
              <div className="vr2-sc-add mt-3">
                <label className="form-label required">{t("Subject")}</label>
                <input
                  type="text"
                  className="form-control form-control-sm mb-2"
                  value={newSubject}
                  onChange={(e) => setNewSubject(e.target.value)}
                  autoFocus
                />
                <label className="form-label required">{t("Description")}</label>
                <input
                  type="text"
                  className="form-control form-control-sm mb-3"
                  value={newDescription}
                  onChange={(e) => setNewDescription(e.target.value)}
                />
                <div className="d-flex justify-content-end gap-2">
                  <button type="button" className="btn btn-sm btn-light" onClick={() => setAdding(false)}>
                    {t("Cancel")}
                  </button>
                  <button type="button" className="btn btn-sm btn-primary" onClick={addReason}>
                    {t("Add Reason")}
                  </button>
                </div>
              </div>
            ) : (
              <button
                type="button"
                className="btn btn-link btn-sm px-0 mt-2"
                onClick={() => setAdding(true)}
              >
                <i className="bi bi-plus-lg me-1"></i>
                {t("Add Another Reason")}
              </button>
            )}

            <label className="form-label mt-4">{t("Rejection Comment")}</label>
            <textarea
              className="form-control bg-transparent h-60px"
              placeholder={t("Rejection Comments")}
              value={reason}
              onChange={(e) => setReason(e.target.value)}
            />
          </>
        ) : (
          <>
            {action === StatusAction.Delete && (
              <p className="text-gray-700 mb-3">
                {t("Are you sure you want to delete the selected requisition(s)?")}
              </p>
            )}
            <label className="mb-2 required form-label">{t("Reason")}</label>
            <input
              type="text"
              className="form-control bg-transparent"
              placeholder={t("Enter Reasons")}
              value={reason}
              autoFocus
              onChange={(e) => setReason(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && save()}
            />
          </>
        )}
        {error && <div className="text-danger fs-7 mt-2">{error}</div>}
      </BootstrapModal.Body>
      <BootstrapModal.Footer className="py-3 px-6">
        <button type="button" className="btn btn-sm btn-light" onClick={onClose}>
          {t("Cancel")}
        </button>
        <button
          type="button"
          className={`btn btn-sm ${
            action === StatusAction.Delete || isReject ? "btn-danger" : "btn-primary"
          }`}
          disabled={saving}
          onClick={save}
        >
          {saving && <LoaderIcon />} {t(action === StatusAction.Delete ? "Delete" : "Save")}
        </button>
      </BootstrapModal.Footer>
    </BootstrapModal>
  );
};

export default StatusChangeModal;
